import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  SageBuildResult,
  SageBatchPage,
  SageBatchDetail,
  SageBatchCsv,
  SageConfirmResult,
  SageTaxCode
} from '../models/sage-export.model';

/**
 * Client for the Sage 50 export.
 *
 * Two halves that are deliberately separate calls, usually minutes apart and often different
 * people: building the file, and recording what Sage did with it. Nothing here should ever
 * collapse the two - a built file is not an invoice in the accounts.
 */
@Injectable({
  providedIn: 'root'
})
export class SageExportService {

  /**
  * Endpoint for http calls
  */
  private _endpoint: string = environment.api.endpoint;

  constructor(private httpClient: HttpClient) { }

  /**
  * Builds a batch of everything unconfirmed invoiced on or before `upTo`, and returns the file.
  *
  * @param upTo YYYY-MM-DD
  *
  * @returns Observable<SageBuildResult>
  */
  build(upTo: string): Observable<SageBuildResult> {

    if (!upTo || upTo === '') {
      throw new Error('SageExportService: you must supply an upTo date for build');
    }

    return this.httpClient.post(this._endpoint + 'sage-export/build/',
      JSON.stringify({ upTo: upTo }),
      { headers: { 'Content-Type': 'application/json' } }).pipe(map((value: any) => {
        return <SageBuildResult>value;
      }));
  }

  /**
  * A page of past batches.
  *
  * @param limit rows per page
  * @param offset rows to skip
  * @param state all | outstanding | confirmed
  *
  * @returns Observable<SageBatchPage>
  */
  getBatches(limit: number, offset: number, state: string): Observable<SageBatchPage> {

    if (!limit || limit < 1) {
      throw new Error('SageExportService: limit must be a positive number in getBatches');
    }

    if (offset === null || offset === undefined || offset < 0) {
      throw new Error('SageExportService: offset must be zero or greater in getBatches');
    }

    return this.httpClient.get(this._endpoint + 'sage-export/batches/'
      + '?limit=' + limit + '&offset=' + offset + '&state=' + encodeURIComponent(state || 'all'))
      .pipe(map((value: any) => {
        return <SageBatchPage>value;
      }));
  }

  /**
  * One batch and every document in it.
  *
  * @param batchRef batch reference
  *
  * @returns Observable<SageBatchDetail>
  */
  getBatch(batchRef: string): Observable<SageBatchDetail> {

    if (!batchRef || batchRef === '') {
      throw new Error('SageExportService: you must supply a batchRef for getBatch');
    }

    return this.httpClient.get(this._endpoint + 'sage-export/batches/' + encodeURIComponent(batchRef) + '/')
      .pipe(map((value: any) => {
        return <SageBatchDetail>value;
      }));
  }

  /**
  * The stored file for a batch. 404s for batches built before the file was retained.
  *
  * @param batchRef batch reference
  *
  * @returns Observable<SageBatchCsv>
  */
  getBatchCsv(batchRef: string): Observable<SageBatchCsv> {

    if (!batchRef || batchRef === '') {
      throw new Error('SageExportService: you must supply a batchRef for getBatchCsv');
    }

    return this.httpClient.get(this._endpoint + 'sage-export/batches/' + encodeURIComponent(batchRef) + '/csv/')
      .pipe(map((value: any) => {
        return <SageBatchCsv>value;
      }));
  }

  /**
  * Records which documents Sage accepted. The only thing that takes a document out of the next
  * batch.
  *
  * Both id arrays are required even when empty, which is why they are not defaulted here: an
  * absent list would be indistinguishable from "Sage took none of them", and the backend refuses
  * rather than guess.
  *
  * @param batchRef batch reference
  * @param acceptedInvoiceIds invoice ids Sage accepted
  * @param acceptedCreditNoteIds credit note ids Sage accepted
  * @param outcome what Sage reported
  *
  * @returns Observable<SageConfirmResult>
  */
  confirm(batchRef: string, acceptedInvoiceIds: number[], acceptedCreditNoteIds: number[],
    outcome: string): Observable<SageConfirmResult> {

    if (!batchRef || batchRef === '') {
      throw new Error('SageExportService: you must supply a batchRef for confirm');
    }

    if (!Array.isArray(acceptedInvoiceIds) || !Array.isArray(acceptedCreditNoteIds)) {
      throw new Error('SageExportService: accepted ids must be arrays in confirm');
    }

    return this.httpClient.post(this._endpoint + 'sage-export/confirm/',
      JSON.stringify({
        batchRef: batchRef,
        acceptedInvoiceIds: acceptedInvoiceIds,
        acceptedCreditNoteIds: acceptedCreditNoteIds,
        outcome: outcome
      }),
      { headers: { 'Content-Type': 'application/json' } }).pipe(map((value: any) => {
        return <SageConfirmResult>value;
      }));
  }

  getTaxCodes(): Observable<SageTaxCode[]> {
    return this.httpClient.get(this._endpoint + 'sage-export/tax-codes/').pipe(map((value: any) => {
      return <SageTaxCode[]>value;
    }));
  }

  /**
  * Sets the Sage tax code on a tax type.
  *
  * @param taxTypeId tax type to set it on
  * @param sageCode Sage's code, e.g. T1
  */
  setTaxCode(taxTypeId: number, sageCode: string): Observable<any> {

    if (!taxTypeId || taxTypeId <= 0) {
      throw new Error('SageExportService: taxTypeId must be valid in setTaxCode');
    }

    return this.httpClient.post(this._endpoint + 'sage-export/tax-codes/',
      JSON.stringify({ taxTypeId: taxTypeId, sageCode: sageCode }),
      { headers: { 'Content-Type': 'application/json' } });
  }

  /**
  * Saves the batch file to disk, byte for byte.
  *
  * NOT a general CSV builder and it must never grow into one. It takes a finished Sage file and
  * writes it out unchanged: no re-quoting, no newline normalising, no BOM, no transformation of
  * any kind. The bytes the backend produced are the bytes Sage reads.
  *
  * Each of those matters. The backend emits CRLF because Sage 50 is a Windows application whose
  * import has historically been unforgiving about bare newlines. A UTF-8 BOM would land inside
  * the first header cell. And quoting an already-quoted file doubles every quote in it.
  *
  * This is why `warehousing/services/report-csv.service.ts` is mirrored rather than reused - its
  * contract is (filename, headers, rows), it quotes every cell and joins with '\n', and it ships
  * the result through a `data:` URI, which is the wrong shape for a file of several hundred
  * lines.
  *
  * @param batchRef used as the filename, so the file on disk names the batch to confirm against
  * @param csv the file exactly as the backend produced it
  */
  downloadCsv(batchRef: string, csv: string): void {

    if (!batchRef || batchRef === '') {
      throw new Error('SageExportService: you must supply a batchRef for downloadCsv');
    }

    if (csv === null || csv === undefined) {
      throw new Error('SageExportService: you must supply the csv for downloadCsv');
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', batchRef + '.csv');
    document.body.appendChild(link); // Required for FF

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
  * Every reason a batch was refused, as a list, with the lead sentence first.
  *
  * The backend refuses the whole batch and returns all the faults at once, so that one run tells
  * the operator everything to fix rather than one thing per attempt. That is only worth anything
  * if all of them reach the screen, hence a list rather than a message.
  *
  * Index 0 is ALWAYS the lead sentence and never a fault, because the caller renders it as the
  * heading and the rest as the list. The two error shapes disagree about this and the difference
  * is easy to miss: in `message` the lead sentence is the first line, but in `faults` every entry
  * is a fault. Reading `faults[0]` as the heading therefore promoted a real fault into the title
  * and dropped it from the list - on a refusal with one fault, the list rendered empty. So the
  * lead is taken from `message` here, or synthesised, and prepended.
  *
  * Returns an empty array when nothing usable is in the error, so the caller can show a generic
  * failure instead of silently rendering nothing.
  *
  * @param error the HttpErrorResponse
  *
  * @returns string[] the lead sentence, then one entry per fault
  */
  faultsFrom(error: any): string[] {

    const body: any = error && error.error ? error.error : null;

    if (body && Array.isArray(body.faults)) {
      const faults: string[] = this.clean(body.faults);

      if (faults.length > 0) {
        const lead: string = this.leadOf(body.message)
          || ('The batch was not created, because ' + faults.length
            + ' problem(s) would have been sent to Sage or silently left behind:');

        return faults.indexOf(lead) === -1 ? [lead].concat(faults) : faults;
      }
    }

    return this.clean(this.textOf(body && body.message ? body.message : (error ? error.message : '')));
  }

  /** The first non-empty line of a message, which is where the backend puts the lead sentence. */
  private leadOf(message: any): string {
    const lines: string[] = this.clean(this.textOf(message));

    return lines.length > 0 ? lines[0] : '';
  }

  /** Nest sends `message` as an array for validation failures and a string otherwise. */
  private textOf(message: any): string[] {
    const text: string = Array.isArray(message) ? message.join('\n') : String(message || '');

    return text.split(/\r?\n/);
  }

  private clean(lines: any[]): string[] {
    return lines
      .map((line: any) => String(line === null || line === undefined ? '' : line).trim())
      .filter((line: string) => line !== '');
  }
}
